import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { CSVLink } from 'react-csv';
import withRoleAccess from '../../hoc/withRoleAccess';
import {
  Table,
  Input,
  Card,
  Typography,
  Tag,
  Tooltip,
  Spin,
  Space,
  Divider,
  message,
  Badge,
  Button,
  Select
} from 'antd';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  BookOutlined,
  FileTextOutlined,
  SyncOutlined,
  CloseCircleOutlined,
    PauseCircleOutlined,
    ExclamationCircleOutlined,
    ReconciliationOutlined,
    QuestionCircleOutlined,
    FileExcelOutlined,
    ScissorOutlined
} from '@ant-design/icons';
import { jwtDecode } from "jwt-decode";

const { Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const ClassChange = () => {
  const { i18n, t } = useTranslation();
  const [classChangeLogs, setClassChangeLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [branches, setBranches] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [userBranchId, setUserBranchId] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [loading, setLoading] = useState(true);

  // Default date range = current month (plain JS, no antd DatePicker)
  const getCurrentMonthRange = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { start: fmt(first), end: fmt(last) };
  };
  const monthRange = getCurrentMonthRange();
  const [startDate, setStartDate] = useState(monthRange.start);
  const [endDate, setEndDate] = useState(monthRange.end);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  });

     useEffect(() => {
  const token = localStorage.getItem("token");
  if (token) {
    try {
      const decoded = jwtDecode(token);
      setUserRole(decoded.role);
      setUserBranchId(decoded.branchId || decoded.branch_id || null);
    } catch (err) {
      console.error("Invalid token", err);
    }
  }
}, []);
  
  const API_URL = process.env.REACT_APP_API_URL;

  const formatTime = (timeString) => {
    try {
      const [hours, minutes] = timeString.split(':');
      return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
    } catch (error) {
      console.error('Invalid time value:', timeString);
      return t('invalidTime');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return new Intl.DateTimeFormat(i18n.language, options).format(date);
  };

  const formatDateTime = (dateTimeString) => {
    const dateTime = new Date(dateTimeString);
    const options = { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    };
    return new Intl.DateTimeFormat(i18n.language, options).format(dateTime);
  };

  const calculateDuration = (startTime, endTime) => {
    try {
      const start = new Date(`1970-01-01T${startTime}Z`);
      const end = new Date(`1970-01-01T${endTime}Z`);
      const diff = (end - start) / (1000 * 60); // difference in minutes

      const hours = Math.floor(diff / 60);
      const minutes = diff % 60;

      return `${hours}:${minutes.toString().padStart(2, '0')} ${t('hr')}`;
    } catch (error) {
      console.error('Error calculating duration:', error);
      return t('invalidDuration');
    }
  };

  const getChangeReasonTag = (reason) => {
    const reasonMap = {
      'admin_adjust': { color: 'blue', icon: <SyncOutlined />, text: 'Admin' },
      'cancelled_by_student': { color: 'red', icon: <CloseCircleOutlined />, text: 'CL by S.' },
      'cancelled_by_teacher': { color: 'orange', icon: <CloseCircleOutlined />, text: 'CL by T.' },
      'class_on_hold_by_student': { color: 'geekblue', icon: <PauseCircleOutlined />, text: 'On hold by S.' },
      'class_on_hold_by_school': { color: 'green', icon: <PauseCircleOutlined />, text: 'On hold by P.' },
      'cancelled_same_day_teacher': { color: 'volcano', icon: <ExclamationCircleOutlined />, text: 'CSD (Teacher)' },
      'cancelled_same_day_student': { color: 'volcano', icon: <ExclamationCircleOutlined />, text: 'CSD (Student)' },
      'teacher_late': { color: 'gold', icon: <ClockCircleOutlined />, text: 'Teacher Late' },
      'kease_cut': { color: 'magenta', icon: <ScissorOutlined />, text: 'KEAES Cut' },
      'make_up': { color: 'purple', icon: <ReconciliationOutlined />, text: 'Make Up' },
      'others': { color: 'default', icon: <QuestionCircleOutlined />, text: 'Others' }
    };

    const config = reasonMap[reason] || { color: 'default', text: reason };
    
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

    useEffect(() => {
  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/branch`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(res.data);
    } catch (err) {
      console.error("Failed to fetch branches", err);
    }
  };

  fetchBranches();
}, [API_URL]);


const fetchClassChangeLogs = useCallback(async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("❌ No token found");
      setLoading(false);
      return;
    }

    // Build params object
    const params = {};

    // For admin: always filter by their branch
    if (userRole === "admin" && userBranchId) {
      params.branchId = userBranchId;
    }

    // For superadmin: filter by selected branch if one is chosen
    if (userRole === "superadmin" && selectedBranch) {
      params.branchId = selectedBranch;
    }

    // If superadmin hasn't selected a branch, show all branches
    if (userRole === "superadmin" && !selectedBranch) {
    }

    // Date range filter (defaults to current month)
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await axios.get(
      `${API_URL}/class-change/class-change-logs`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params,
      }
    );
    
    setClassChangeLogs(response.data);
    setFilteredLogs(response.data);
    setPagination((prev) => ({
      ...prev,
      total: response.data.length,
      current: 1, // Reset to first page when data changes
    }));
  } catch (error) {
    console.error("❌ Error fetching class change logs:", error);
    message.error("Failed to load class change logs");
  } finally {
    setLoading(false);
  }
}, [API_URL, userRole, userBranchId, selectedBranch, startDate, endDate]); // ✅ Include all dependencies

// 👇 keep your fetchClassChangeLogs exactly as is
useEffect(() => {
  fetchClassChangeLogs();
}, [fetchClassChangeLogs]);

// ✅ add this effect to recalc filteredLogs
// Add this to your filtering useEffect to see what's happening:
useEffect(() => {
  let logs = [...classChangeLogs];

  // Only do search filtering on frontend now
  if (searchTerm && searchTerm.trim()) {
    const term = searchTerm.toLowerCase().trim();
    logs = logs.filter((log) => {
      return (
        log.classid?.toString().toLowerCase().includes(term) ||
        log.subject_name?.toLowerCase().includes(term) ||
        log.class_code?.toLowerCase().includes(term) ||
        log.nickname?.toLowerCase().includes(term)
      );
    });
  }

  setFilteredLogs(logs);
  setPagination(prev => ({
    ...prev,
    current: 1,
    total: logs.length,
  }));
}, [classChangeLogs, searchTerm]); // Remove selectedBranch from dependencies

// ✅ Simplified search handler
const handleSearch = (value) => {
  setSearchTerm(value);
};

// ✅ Branch change handler
const handleBranchChange = (value) => {
  setSelectedBranch(value);
  setSearchTerm("");
};

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const tableKey = `${filteredLogs.length}-${searchTerm}-${selectedBranch}`;

    const prepareCSVData = () => {
      return filteredLogs.map(log => ({
        'Branch': log.branch_name || `Branch ${log.branch_id || ''}`, 
        'Class ID': log.classid,
        'Class Code': log.class_code,
        'Subject': log.subject_name,
        'Teacher': log.nickname,
        'Original Date': formatDate(log.originaldate),
        'Original Time': `${formatTime(log.originalstarttime)} - ${formatTime(log.originalendtime)}`,
        'Original Hours': calculateDuration(log.originalstarttime, log.originalendtime),
        'New Date': formatDate(log.newdate),
        'New Time': `${formatTime(log.newstarttime)} - ${formatTime(log.newendtime)}`,
        'New Hours': calculateDuration(log.newstarttime, log.newendtime),
        'Change Reason': log.changereason,
        'Note': log.note || '',
        'Updated By': log.updateby,
        'Updated At': formatDateTime(log.updated_at)
      }));
    };
    const branchColors = {
  1: "blue",
  2: "green",
  3: "magenta", 
};

  const columns = [
    {
  title: t('branch'),
  dataIndex: 'branch_name',
  key: 'branch_name',
  width: 120,
  render: (text, record) => {
    const color = branchColors[record.branch_id] || "default";
    return <Tag color={color}>{text || t("N/A")}</Tag>;
  }
},
    {
      title: t('classcode'),
      dataIndex: 'class_code',
      key: 'class_code',
      width: 250,
      fixed: 'left',
      render: code => (
        <Badge
          status="processing"
          text={
            <span style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word', display: 'inline-block', lineHeight: '1.3' }}>
              {code}
            </span>
          }
        />
      )
    },
    {
      title: `${t('id')} / ${t('subject')} / ${t('teacher')}`,
      key: 'class_info',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Text strong>#{record.classid}</Text>
          <Space size={4} align="start">
            <BookOutlined />
            <Text style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{record.subject_name}</Text>
          </Space>
          <Space size={4}>
            <UserOutlined />
            <Text type="secondary">{record.nickname}</Text>
          </Space>
        </Space>
      )
    },
    {
      title: t('From'),
      key: 'from',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          <Space size={4}>
            <CalendarOutlined />
            <Text>{formatDate(record.originaldate)}</Text>
          </Space>
          <Tag icon={<ClockCircleOutlined />} color="default">
            {formatTime(record.originalstarttime)} - {formatTime(record.originalendtime)} ({calculateDuration(record.originalstarttime, record.originalendtime)})
          </Tag>
        </Space>
      )
    },
    {
      title: t('To'),
      key: 'to',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          <Space size={4}>
            <CalendarOutlined />
            <Text>{formatDate(record.newdate)}</Text>
          </Space>
          <Tag icon={<ClockCircleOutlined />} color="blue">
            {formatTime(record.newstarttime)} - {formatTime(record.newendtime)} ({calculateDuration(record.newstarttime, record.newendtime)})
          </Tag>
        </Space>
      )
    },
    {
      title: t('changereason'),
      dataIndex: 'changereason',
      key: 'changereason',
      width: 130,
      render: reason => getChangeReasonTag(reason)
    },
    {
      title: t('note'),
      dataIndex: 'note',
      key: 'note',
      width: 200,
      render: note => (
        <Tooltip title={note} placement="topLeft">
          <Text ellipsis style={{ maxWidth: 180 }}>
            <FileTextOutlined style={{ marginRight: 8 }} />
            {note || '-'}
          </Text>
        </Tooltip>
      )
    },
    {
        title: t('changed by'),
        dataIndex: 'updateby',
        key: 'updateby',
        width: 120,
        render: (text) => (
          <Tag color="volcano" style={{ fontSize: '11px', padding: '0 6px' }}>
            {text || '-'}
          </Tag>
        ),
      },
    {
      title: t('updateat'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 160,
      render: date => formatDateTime(date)
    },
  ];

  return (
    <div className="class-change-container">      
      <Card className="search-card">
  <Space direction="vertical" style={{ width: '100%' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>

      {/* Branch Selection for Superadmin */}
        {userRole === 'superadmin' && (
            <Space>
              <Select
                  style={{ minWidth: '200px', marginRight: '12px' }}
                  value={selectedBranch}
                  onChange={handleBranchChange} 
                  placeholder={t('All Branches')}
                  allowClear
                >
                  {branches.map(branch => (
                    <Option key={branch.id} value={branch.id}>
                      {branch.name}
                    </Option>
                  ))}
                </Select>
            </Space>
        )}
      {/* Plain JS date range filter (no antd DatePicker) */}
      <Space size={4} style={{ marginRight: '12px' }}>
        <input
          type="date"
          value={startDate}
          max={endDate || undefined}
          onChange={(e) => setStartDate(e.target.value)}
          style={{ padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: '6px', height: '32px' }}
        />
        <span>-</span>
        <input
          type="date"
          value={endDate}
          min={startDate || undefined}
          onChange={(e) => setEndDate(e.target.value)}
          style={{ padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: '6px', height: '32px' }}
        />
      </Space>
      <Search
        placeholder={t('Search by Class ID, Code, Subject or Teacher')}
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        onSearch={handleSearch}
        enterButton
        allowClear
        style={{ flex: 1, minWidth: '200px' }}
      />
      <CSVLink
        data={prepareCSVData()}
        filename={`class-changes-${new Date().toISOString().slice(0, 10)}.csv`}
        className="ant-btn ant-btn-primary"
        style={{ marginLeft: '10px' }}
      >
        <Button type="primary" icon={<FileExcelOutlined />}>
              {t("Export CSV")}
            </Button>
      </CSVLink>
    </div>
    <Text type="secondary">
      {filteredLogs.length} {t('records found')}
    </Text>
  </Space>
</Card>
      
      <Divider />
      
      <Spin spinning={loading} tip={t('Loading logs...')}>
        <Table
            key={tableKey} // 🔥 This forces a complete re-render
            columns={columns}
            dataSource={filteredLogs}
            rowKey={(record) => `${record.classid}-${record.originaldate}-${record.originalstarttime}`} // More unique key
            pagination={{
              ...pagination,
              total: filteredLogs.length, // Explicitly set total
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              pageSize: pagination.pageSize || 10,
              current: pagination.current || 1,
            }}
            onChange={handleTableChange}
            scroll={{ x: 'max-content', y: 'calc(100vh - 300px)' }}
            size="middle"
            bordered
          />
      </Spin>
    </div>
  );
};

export default withRoleAccess(ClassChange, ['admin', 'superadmin']);